import { ApiChartAttribute } from "@incendium/api";
import { ComponentType, createElement, ElementType } from "react";
import {
  ArticleOutlined,
  CampaignOutlined,
  Inventory2Outlined,
  PaidOutlined,
  PeopleOutline,
  SpeedOutlined,
  TimelineOutlined,
  TrackChangesOutlined,
  TrendingUp,
} from "@mui/icons-material";
import PulseTab from "features/dashboard/components/PulseTab";
import MarketingTab from "features/dashboard/components/MarketingTab";
import ProductsTab from "features/dashboard/components/ProductsTab";
import CustomerTab from "features/dashboard/components/CustomerTab";
import PaidMediaTab from "features/dashboard/components/PaidMediaTab";
import StoreOverviewTab from "features/dashboard/components/StoreOverviewTab";
import ProfitabilityTab from "features/dashboard/components/ProfitabilityTab";
import ChannelMixTab from "features/dashboard/components/ChannelMixTab";
import EmailTab from "features/dashboard/components/EmailTab";
import NewReturningTab from "features/dashboard/components/NewReturningTab";
import LtvTab from "features/dashboard/components/LtvTab";
import BestsellersTab from "features/dashboard/components/BestsellersTab";
import CategoriesBrandsTab from "features/dashboard/components/CategoriesBrandsTab";
import AttributionOverviewTab from "features/dashboard/components/AttributionOverviewTab";
import CampaignsTab from "features/dashboard/components/CampaignsTab";
import CoreWebVitalsTab from "features/dashboard/components/CoreWebVitalsTab";
import SlowPagesTab from "features/dashboard/components/SlowPagesTab";
import SpeedByLandingTab from "features/dashboard/components/SpeedByLandingTab";
import LandingPagesTab from "features/dashboard/components/LandingPagesTab";
import ContentPerformanceTab from "features/dashboard/components/ContentPerformanceTab";
import PagesTab from "features/dashboard/components/PagesTab";
import SiteOverviewTab from "features/dashboard/components/SiteOverviewTab";
import DevicesTab from "features/dashboard/components/DevicesTab";
import FunnelTab from "features/dashboard/components/FunnelTab";
import PlaceholderReportTab from "features/dashboard/components/PlaceholderReportTab";

/**
 * Dashboard reports are intentionally thin (usually 2–3 charts).
 * Each one should reuse sections from an existing Analyse report rather
 * than inventing net-new chart logic.
 */
export type DashboardReportId =
  // Revenue ← Ecom Insights + Store Overview
  | "pulse"
  | "store-overview"
  | "profitability"
  // Marketing ← Traffic & Campaigns + Site Overview + Email
  | "marketing"
  | "channel-mix"
  | "email"
  // Customers ← Store Customers & Retention + Ecom New/Returning + LTV
  | "customers"
  | "new-returning"
  | "ltv"
  // Products ← Store Product Performance + Store Overview + Products Overview
  | "products"
  | "bestsellers"
  | "categories-brands"
  // Attribution ← Paid Media + Attribution Insights + Ecom Campaigns
  | "paid-media"
  | "attribution-overview"
  | "campaigns"
  // Site Performance ← CWV / page speed from Content & Locations
  | "core-web-vitals"
  | "slow-pages"
  | "speed-by-landing"
  // Content ← Landing Pages + Content Performance + Locations
  | "landing-pages"
  | "content-performance"
  | "pages"
  // Engagement ← Site Overview + Devices + Funnel
  | "site-overview"
  | "devices"
  | "funnel";

export type DashboardReportCategoryId =
  | "revenue"
  | "marketing"
  | "customers"
  | "products"
  | "attribution"
  | "site-performance"
  | "content"
  | "engagement";

export interface DashboardReportDefinition {
  id: DashboardReportId;
  label: string;
  description: string;
  /** Full Analyse report this strip is based on */
  sourceReport: string;
  /** Charts / sections to reuse (2–3 per tab) */
  borrows: string[];
  categoryId: DashboardReportCategoryId;
  /** False when the tab UI is still a placeholder */
  available?: boolean;
}

export interface DashboardReportCategory {
  id: DashboardReportCategoryId;
  label: string;
  description: string;
  icon: ElementType;
}

export interface DashboardProfile {
  id: string;
  name: string;
  description?: string;
  reportIds: DashboardReportId[];
  isPreset?: boolean;
}

export type DashboardReportComponentProps = {
  attributes: ApiChartAttribute[];
  reportId: DashboardReportId;
};

export const DASHBOARD_REPORT_CATEGORIES: DashboardReportCategory[] = [
  {
    id: "revenue",
    label: "Revenue",
    description: "Track sales, revenue and monetization.",
    icon: PaidOutlined,
  },
  {
    id: "marketing",
    label: "Marketing",
    description: "Monitor campaigns and channel performance.",
    icon: CampaignOutlined,
  },
  {
    id: "customers",
    label: "Customers",
    description: "Understand user growth and engagement.",
    icon: PeopleOutline,
  },
  {
    id: "products",
    label: "Products",
    description: "Analyze product usage and adoption.",
    icon: Inventory2Outlined,
  },
  {
    id: "attribution",
    label: "Attribution",
    description: "Measure what drives conversions.",
    icon: TrackChangesOutlined,
  },
  {
    id: "site-performance",
    label: "Site Performance",
    description: "Monitor page speed and Core Web Vitals.",
    icon: SpeedOutlined,
  },
  {
    id: "content",
    label: "Content",
    description: "Landing pages, content and page performance.",
    icon: ArticleOutlined,
  },
  {
    id: "engagement",
    label: "Engagement",
    description: "Traffic quality, devices and conversion funnels.",
    icon: TimelineOutlined,
  },
];

export const DASHBOARD_REPORTS: DashboardReportDefinition[] = [
  // ── Revenue ──────────────────────────────────────────────────────────────
  {
    id: "pulse",
    label: "Pulse",
    description: "Key ecom stats, funnel, and revenue trend.",
    sourceReport: "Ecom Insights → Overview",
    borrows: [
      "EcomStatsAndFunnel (Revenue / Margin / ROI / MER)",
      "Ecommerce Conversion Funnel",
      "Sessions & Revenue trend",
    ],
    categoryId: "revenue",
    available: true,
  },
  {
    id: "store-overview",
    label: "Store Overview",
    description: "Orders, AOV, and revenue over time.",
    sourceReport: "Store Intelligence → Overview",
    borrows: [
      "Orders / Items / AOV / Customers stats",
      "Revenue Over Time",
      "Top Products table",
    ],
    categoryId: "revenue",
    available: true,
  },
  {
    id: "profitability",
    label: "Profitability",
    description: "Margin, MER, and LTV:CAC performance.",
    sourceReport: "Ecom Insights → Overview",
    borrows: [
      "Contribution Margin trend",
      "MER trend",
      "LTV:CAC + Net Revenue stats",
    ],
    categoryId: "revenue",
    available: true,
  },

  // ── Marketing ────────────────────────────────────────────────────────────
  {
    id: "marketing",
    label: "Channel Insights",
    description: "Top channels and traffic performance.",
    sourceReport: "Quick Insights → Traffic & Campaigns",
    borrows: ["Top 5 Channels trend", "Traffic & Performance table"],
    categoryId: "marketing",
    available: true,
  },
  {
    id: "channel-mix",
    label: "Channel Mix",
    description: "How channels contribute to visits and sales.",
    sourceReport: "Site Overview + Ecom Insights",
    borrows: [
      "Channel composition pie",
      "Top channels by revenue",
      "Channel mix table",
    ],
    categoryId: "marketing",
    available: true,
  },
  {
    id: "email",
    label: "Email",
    description: "Email campaign sessions and revenue.",
    sourceReport: "Organic & Owned → Email Marketing",
    borrows: [
      "Email key stats",
      "Sessions / revenue by campaign",
      "Campaigns table",
    ],
    categoryId: "marketing",
    available: true,
  },

  // ── Customers ────────────────────────────────────────────────────────────
  {
    id: "customers",
    label: "Retention",
    description: "Repeat purchase trends and cohorts.",
    sourceReport: "Store Intelligence → Customers & Retention",
    borrows: ["RetentionMetricsOverTime", "CustomerRetentionTable"],
    categoryId: "customers",
    available: true,
  },
  {
    id: "new-returning",
    label: "New & Returning",
    description: "New vs returning customer performance.",
    sourceReport: "Ecom Insights → New / Returning + Store Overview",
    borrows: [
      "New / Returning stats",
      "New vs Returning pie",
      "Revenue by customer type trend",
    ],
    categoryId: "customers",
    available: true,
  },
  {
    id: "ltv",
    label: "Lifetime Value",
    description: "Customer LTV cohorts and value trends.",
    sourceReport: "Audiences → LTV Analysis",
    borrows: [
      "Customer Value Cohort grid",
      "LTV:CAC trend (from Ecom Insights)",
    ],
    categoryId: "customers",
    available: true,
  },

  // ── Products ─────────────────────────────────────────────────────────────
  {
    id: "products",
    label: "Product Performance",
    description: "Product trends and performance table.",
    sourceReport: "Store Intelligence → Product Performance",
    borrows: ["ProductAnalyticsCarousel", "ProductPerformanceTable"],
    categoryId: "products",
    available: true,
  },
  {
    id: "bestsellers",
    label: "Bestsellers",
    description: "Top sellers and product movers.",
    sourceReport: "Store Overview + Ecom Insights → Products",
    borrows: [
      "Top Performer highlight",
      "Top products by revenue bar",
      "Top Products table",
    ],
    categoryId: "products",
    available: true,
  },
  {
    id: "categories-brands",
    label: "Categories & Brands",
    description: "Category and brand performance.",
    sourceReport: "Products → Overview",
    borrows: [
      "Top Category / Brand",
      "Category insights table",
      "Brand insights table",
    ],
    categoryId: "products",
    available: true,
  },

  // ── Attribution ──────────────────────────────────────────────────────────
  {
    id: "paid-media",
    label: "Paid Media",
    description: "Paid spend, clicks, and ROAS table.",
    sourceReport: "Ad Optimization → Performance Overview",
    borrows: [
      "Source / Campaign / Content / Term toggle",
      "Spend / CPC / Clicks / ROAS table",
    ],
    categoryId: "attribution",
    available: true,
  },
  {
    id: "attribution-overview",
    label: "Attribution Overview",
    description: "Spend, sales, and ROI by attributed channel.",
    sourceReport: "Paid Media → Attribution Insights",
    borrows: ["AttributionHeader trend", "Spend pie + Sales + ROI by platform"],
    categoryId: "attribution",
    available: true,
  },
  {
    id: "campaigns",
    label: "Campaigns",
    description: "Paid campaign performance breakdown.",
    sourceReport: "Ecom Insights → Campaigns + Paid Media",
    borrows: ["Attribution header", "Paid campaign performance table"],
    categoryId: "attribution",
    available: true,
  },

  // ── Site Performance (speed) ─────────────────────────────────────────────
  {
    id: "core-web-vitals",
    label: "Core Web Vitals",
    description: "Overall CWV score and page experience.",
    sourceReport: "Content → Locations Overview / Content Performance",
    borrows: [
      "Average CWV performance score",
      "CWV by page / landing location",
      "Pages needing speed attention",
    ],
    categoryId: "site-performance",
    available: true,
  },
  {
    id: "slow-pages",
    label: "Slow Pages",
    description: "Pages with the weakest speed scores.",
    sourceReport: "Content → Content Performance Analysis",
    borrows: [
      "Content to Improve (CWV weighted)",
      "Page ranking by CWV",
      "Top / worst page comparison",
    ],
    categoryId: "site-performance",
    available: true,
  },
  {
    id: "speed-by-landing",
    label: "Speed by Landing",
    description: "CWV and load quality on landing pages.",
    sourceReport: "Content → Locations Overview + Organic Landing Pages",
    borrows: [
      "Landing page location CWV table",
      "Organic landing CWV metrics",
      "Time on page vs speed",
    ],
    categoryId: "site-performance",
    available: true,
  },

  // ── Content ──────────────────────────────────────────────────────────────
  {
    id: "landing-pages",
    label: "Landing Pages",
    description: "Top landing pages by traffic and sales.",
    sourceReport: "Quick Insights → Landing Pages",
    borrows: [
      "Top 5 Landing Locations trend",
      "Landing page composition pie",
      "Landing performance table",
    ],
    categoryId: "content",
    available: true,
  },
  {
    id: "content-performance",
    label: "Content Performance",
    description: "Top content and pages to improve.",
    sourceReport: "Content → Content Performance Analysis",
    borrows: [
      "Top Content tab",
      "Content to Improve",
      "Pageview performance ranking",
    ],
    categoryId: "content",
    available: true,
  },
  {
    id: "pages",
    label: "Pages",
    description: "Page and location traffic overview.",
    sourceReport: "Content → Locations Overview",
    borrows: [
      "Landing sessions / pageviews stats",
      "Pages Locations table",
      "Engagement on page stats",
    ],
    categoryId: "content",
    available: true,
  },

  // ── Engagement ───────────────────────────────────────────────────────────
  {
    id: "site-overview",
    label: "Site Overview",
    description: "Traffic, engagement, and conversion stats.",
    sourceReport: "Quick Insights → Site Overview",
    borrows: [
      "Traffic / Engagement / Conversion stats",
      "Sitewide Engagement Trend",
      "Top channels",
    ],
    categoryId: "engagement",
    available: true,
  },
  {
    id: "devices",
    label: "Devices",
    description: "Engagement and performance by device.",
    sourceReport: "Quick Insights → Devices",
    borrows: [
      "Device composition pie",
      "Device trend",
      "Device / browser engagement table",
    ],
    categoryId: "engagement",
    available: true,
  },
  {
    id: "funnel",
    label: "Conversion Funnel",
    description: "Browse-to-buy funnel drop-off.",
    sourceReport: "Ecom Insights funnel + Audiences → Funnel Analysis",
    borrows: [
      "Ecommerce Conversion Funnel",
      "Optional Sales Flow stage funnel",
    ],
    categoryId: "engagement",
    available: true,
  },
];

export const DASHBOARD_PRESET_PROFILES: DashboardProfile[] = [
  {
    id: "preset-founder",
    name: "Founder preset",
    description: "A balanced view of revenue, growth, and acquisition.",
    reportIds: [
      "pulse",
      "store-overview",
      "marketing",
      "products",
      "new-returning",
      "funnel",
      "landing-pages",
    ],
    isPreset: true,
  },
  {
    id: "preset-marketing",
    name: "Marketing preset",
    description: "Channels, paid media, attribution, and landing pages.",
    reportIds: [
      "pulse",
      "marketing",
      "channel-mix",
      "paid-media",
      "campaigns",
      "attribution-overview",
      "landing-pages",
    ],
    isPreset: true,
  },
  {
    id: "preset-commerce",
    name: "Commerce preset",
    description: "Orders, catalog performance, and customer value.",
    reportIds: [
      "pulse",
      "store-overview",
      "products",
      "bestsellers",
      "categories-brands",
      "customers",
      "ltv",
    ],
    isPreset: true,
  },
  {
    id: "preset-executive",
    name: "Executive preset",
    description: "High-level revenue, efficiency, and customer value.",
    reportIds: [
      "pulse",
      "profitability",
      "store-overview",
      "ltv",
      "channel-mix",
    ],
    isPreset: true,
  },
  {
    id: "preset-site-content",
    name: "Site & Content preset",
    description: "Speed, pages, and content that drive engagement.",
    reportIds: [
      "site-overview",
      "core-web-vitals",
      "landing-pages",
      "content-performance",
      "pages",
      "devices",
      "funnel",
    ],
    isPreset: true,
  },
];

export const DEFAULT_DASHBOARD_PROFILE = DASHBOARD_PRESET_PROFILES[0];

const AVAILABLE_REPORT_COMPONENTS: Partial<
  Record<DashboardReportId, ComponentType<{ attributes: ApiChartAttribute[] }>>
> = {
  pulse: PulseTab,
  "store-overview": StoreOverviewTab,
  profitability: ProfitabilityTab,
  marketing: MarketingTab,
  "channel-mix": ChannelMixTab,
  email: EmailTab,
  products: ProductsTab,
  bestsellers: BestsellersTab,
  "categories-brands": CategoriesBrandsTab,
  customers: CustomerTab,
  "new-returning": NewReturningTab,
  ltv: LtvTab,
  "paid-media": PaidMediaTab,
  "attribution-overview": AttributionOverviewTab,
  campaigns: CampaignsTab,
  "core-web-vitals": CoreWebVitalsTab,
  "slow-pages": SlowPagesTab,
  "speed-by-landing": SpeedByLandingTab,
  "landing-pages": LandingPagesTab,
  "content-performance": ContentPerformanceTab,
  pages: PagesTab,
  "site-overview": SiteOverviewTab,
  devices: DevicesTab,
  funnel: FunnelTab,
};

export function getReportComponent(
  reportId: DashboardReportId,
): ComponentType<DashboardReportComponentProps> {
  const Available = AVAILABLE_REPORT_COMPONENTS[reportId];

  if (Available) {
    return function AvailableReport({ attributes }) {
      return createElement(Available, { attributes });
    };
  }

  return function ComingSoonReport({ attributes, reportId: id }) {
    return createElement(PlaceholderReportTab, {
      attributes,
      reportId: id,
    });
  };
}

export function getReportsByCategory(
  categoryId: DashboardReportCategoryId,
): DashboardReportDefinition[] {
  return DASHBOARD_REPORTS.filter((report) => report.categoryId === categoryId);
}

export function getCategoriesWithReports(): DashboardReportCategory[] {
  return DASHBOARD_REPORT_CATEGORIES.filter(
    (category) => getReportsByCategory(category.id).length > 0,
  );
}

export function reportsMatch(
  a: DashboardReportId[],
  b: DashboardReportId[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((id, index) => id === sortedB[index]);
}

export function findProfileById(
  profileId: string,
  customProfiles: DashboardProfile[] = [],
): DashboardProfile | undefined {
  return (
    DASHBOARD_PRESET_PROFILES.find((profile) => profile.id === profileId) ||
    customProfiles.find((profile) => profile.id === profileId)
  );
}

/** Report order as shown in the customize dialog (category order, then report order). */
export function getDialogReportOrder(): DashboardReportId[] {
  return DASHBOARD_REPORT_CATEGORIES.flatMap((category) =>
    getReportsByCategory(category.id).map((report) => report.id),
  );
}

export function sortReportIdsByDialogOrder(
  reportIds: DashboardReportId[],
): DashboardReportId[] {
  const order = getDialogReportOrder();
  const selected = new Set(reportIds);
  return order.filter((reportId) => selected.has(reportId));
}

export function getVisibleReports(profile: DashboardProfile) {
  return sortReportIdsByDialogOrder(profile.reportIds)
    .map((reportId) =>
      DASHBOARD_REPORTS.find((report) => report.id === reportId),
    )
    .filter((report): report is DashboardReportDefinition => !!report);
}

export const DASHBOARD_PRESET_ICON = TrendingUp;
